import {
    Component,
    Input,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormArray, FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { HeaderComponent } from "@components/header/header.component";
import { IconButtonComponent } from "@components/icon-button/icon-button.component";
import { uniq } from "lodash";
import { MapClosingPopupEvent } from "~app/common/components/map/map.events";
import { EventService } from "~app/common/services/cqs/event.service";
import { IArbetsorderTransportavtand, IGeolocationDto } from "~app/features/transport/transportplanera/models/queries";

type CheckboxFormGroup<T> = FormGroup<{
    text: FormControl<string>;
    value: FormControl<T>;
    checked: FormControl<boolean>;
}>;

type ViapunktFormGroup = FormGroup<{
    barighetsklasser: FormArray<CheckboxFormGroup<string>>;
    vagmotstand: FormArray<CheckboxFormGroup<number>>;
}>;

@Component({
    selector: "rask-tpl-transportavstand-karta-viapunkt-dialog",
    templateUrl: "./viapunkt-dialog.component.html",
    styleUrls: ["./viapunkt-dialog.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        HeaderComponent,
        IconButtonComponent,
        CheckboxComponent,
        ButtonComponent,
        ReactiveFormsModule
    ]
})
export class TplTransportavstandKartaViapunktDialogComponent implements OnInit {
    arbetsorderTransportavtand: IArbetsorderTransportavtand[];
    form: ViapunktFormGroup;

    point: any;
    uppdateraAllaMottagningsplatser = false;

    private readonly mapservice = inject(EventService);

    // eslint-disable-next-line @angular-eslint/no-input-rename
    @Input("data")
    set data(value: any) {
        this.arbetsorderTransportavtand = value.data.arbetsorderTransportavtand;
        this.point = value.gemometri;

        const barighetsklasser = uniq(this.arbetsorderTransportavtand.map(x => x.barighetsklass));
        const vagmotstand = uniq(this.arbetsorderTransportavtand.map(x => x.vagmotstand));

        this.form = new FormGroup({
            barighetsklasser: new FormArray([
                new FormGroup({
                    text: new FormControl("BK1"),
                    checked: new FormControl(true),
                    value: new FormControl("BK1")
                }),
                new FormGroup({
                    text: new FormControl("BK4"),
                    checked: new FormControl(true),
                    value: new FormControl("BK4")
                })
            ].filter(g => barighetsklasser.includes(g.value.value))),
            vagmotstand: new FormArray([
                new FormGroup({
                    text: new FormControl("Sommar"),
                    checked: new FormControl(true),
                    value: new FormControl(1)
                }),
                new FormGroup({
                    text: new FormControl("Vinter"),
                    checked: new FormControl(true),
                    value: new FormControl(2)
                })
            ].filter(g => vagmotstand.includes(g.value.value)))
        });
    }
    ngOnInit(): void {
        this.form.valueChanges.subscribe(values => {
            const barighetsklasser = values.barighetsklasser
                .filter(g => g.checked)
                .map(g => g.value);
            const vagmotstand = values.vagmotstand
                .filter(g => g.checked)
                .map(g => g.value);

            this.arbetsorderTransportavtand.forEach(aot => {
                const existing = aot.viaPunkter.find(x => x.easting === this.point.x && x.northing === this.point.y);

                if (barighetsklasser.includes(aot.barighetsklass) && vagmotstand.includes(aot.vagmotstand)) {
                    if (existing) {
                        return;
                    }
                    const index = Math.max(0, ...aot.viaPunkter.map(v => v.index));
                    const viapunkt: IGeolocationDto = {
                        easting: this.point.x,
                        northing: this.point.y,
                        index: index + 1
                    };

                    aot.viaPunkter = [
                        ...aot.viaPunkter,
                        viapunkt
                    ];
                } else {
                    aot.viaPunkter = aot.viaPunkter.filter(x => x !== existing);
                }
            });
        });
    }

    close(): void {
        const closeEvent = new MapClosingPopupEvent("viapunktpopup");
        this.mapservice.emit(closeEvent);
    }

    mottagningsplatsToggle(checked: boolean) {
        this.uppdateraAllaMottagningsplatser = checked;
        this.arbetsorderTransportavtand.forEach(aot => {
            const viapunkt = aot.viaPunkter.find(x => x.easting === this.point.x && x.northing === this.point.y);
            if (viapunkt) {
                const barighetsklasser = this.form.value.barighetsklasser
                    .filter(g => g.checked)
                    .map(g => g.value);
                viapunkt.barighetsklasser = checked ? barighetsklasser : null;
            }
        });
    }
}
